/**
 * @fileoverview Streaming contract runtime constants for Gist reader clients.
 *
 * This module defines the canonical message schema contract between the backend
 * streaming infrastructure and all reader clients. The contract guarantees that
 * messages emitted by the backend can be safely consumed by Chrome extensions,
 * web playgrounds, share pages, and admin dashboards without version conflicts.
 *
 * ## Architecture Role
 *
 * This file is the single source of truth for:
 * - Message type identifiers (story_bootstrap, layer_slide, etc.)
 * - Canonical layer names (gist, situation, complication, question, answer,
 *   story, logic, counter-argument, steelman, original)
 * - Story classifications (story, argument)
 * - Runtime type guards for message validation
 *
 * ## Design Principles
 *
 * **Zero dependencies**: This module has NO external dependencies so it can run in:
 * - Chrome extension content scripts and service workers
 * - Cloudflare Workers at the edge
 * - Node.js backend services
 * - Browser-based playgrounds
 * - Unit tests without bundler configuration
 *
 * **Framework-agnostic**: Pure JavaScript with JSDoc types for tooling support.
 * TypeScript definitions live in `index.d.ts` for TypeScript consumers.
 *
 * ## Message Contract Overview
 *
 * The streaming contract defines four message types:
 *
 * 1. **story_bootstrap**: Initial message announcing available layers and classification
 *    - Sent once at stream start
 *    - Contains metadata about the story (classification, layers, custom meta)
 *    - Triggers state initialization in reader-core.js
 *
 * 2. **layer_slide**: Individual content slides for a specific layer
 *    - Sent as content is generated (streaming)
 *    - Contains sequence number for ordering
 *    - Content format varies by layer (headline, text, bullets)
 *    - May include audio URLs for TTS playback
 *
 * 3. **layer_complete**: Signals completion of a layer's generation
 *    - Sent when a layer finishes successfully
 *    - Allows UI to show "complete" status
 *    - No more slides expected for this layer
 *
 * 4. **layer_failed**: Reports layer generation failure
 *    - Sent when generation errors occur
 *    - Includes error code and human-readable message
 *    - Allows UI to show error state
 *
 * ## Usage Patterns
 *
 * ### Backend (message emission):
 * ```javascript
 * import { STREAM_MESSAGE_TYPES, CANONICAL_LAYERS } from '@gist-protocol/core';
 *
 * // Emit bootstrap message
 * yield {
 *   type: STREAM_MESSAGE_TYPES.STORY_BOOTSTRAP,
 *   contentId: storyId,
 *   classification: 'story',
 *   availableLayers: [CANONICAL_LAYERS[0], CANONICAL_LAYERS[1]]
 * };
 * ```
 *
 * ### Client (message validation):
 * ```javascript
 * import { isStoryStreamMessage } from '@gist-protocol/core';
 *
 * eventSource.onmessage = (event) => {
 *   const message = JSON.parse(event.data);
 *   if (isStoryStreamMessage(message)) {
 *     readerCore.ingest(message);
 *   }
 * };
 * ```
 *
 * ## Cross-file Dependencies
 *
 * **Imported by:**
 * - packages/protocol/unified-reader/reader-core.js (state management)
 * - packages/backend/src/lib/share/story-stream.ts (SSE message emission)
 * - packages/backend/src/protocol/story-stream.ts (backend streaming)
 * - packages/chrome-plugin/extension/shared/story-stream-contract.js (extension consumption)
 * - packages/ui/playground/admin/shared/story-stream.js (admin dashboard)
 *
 * **Related files:**
 * - packages/protocol/schema/story-stream.schema.json: JSON Schema definition
 * - packages/protocol/index.d.ts: TypeScript type definitions
 * - packages/protocol/unified-reader/reader-core.js: Consumes validated messages
 *
 * ## Version Management
 *
 * Schema version follows semantic versioning:
 * - MAJOR: Breaking changes to message structure
 * - MINOR: Backward-compatible additions (new layers, optional fields)
 * - PATCH: Documentation or non-functional changes
 *
 * Current version: 0.2.0 (pre-1.0, contract still stabilizing)
 */

/** Canonical message type identifiers emitted by the backend stream. */
export const STREAM_MESSAGE_TYPES = Object.freeze({
  STORY_BOOTSTRAP: 'story_bootstrap',
  LAYER_SLIDE: 'layer_slide',
  LAYER_COMPLETE: 'layer_complete',
  LAYER_FAILED: 'layer_failed'
});

/** Canonical reader layers supported by the unified reader architecture. */
export const CANONICAL_LAYERS = Object.freeze([
  'gist',
  'situation',
  'complication',
  'question',
  'answer',
  'story',
  'logic',
  'counter-argument',
  'steelman',
  'original'
]);

/** Allowed classification categories surfaced during bootstrap. */
export const STORY_CLASSIFICATIONS = Object.freeze([
  'story',
  'argument'
]);

/**
 * Canonical write-side summary versions. New writes must use these values.
 * `isStoryStreamMessage()` also accepts legacy readable `deep` when allowLegacy
 * is set — do not put `deep` back into this write enum.
 */
export const SUMMARY_VERSIONS = Object.freeze(['instant']);

/** Semantic version for the current streaming message contract. */
export const STREAM_MESSAGE_SCHEMA_VERSION = '0.2.0';

/**
 * Runtime type guard to verify unknown data conforms to the streaming contract.
 * This performs coarse validation matching the JSON Schema: string checks,
 * required property presence, and enum membership. Heavyweight validation
 * should use the JSON Schema with a proper validator, but this guard helps
 * filter obvious junk before deeper processing.
 *
 * @param {unknown} value - Unknown payload to inspect.
 * @param {{ allowLegacy?: boolean }} [options] - Validation options.
 *   - allowLegacy: true permits missing summaryVersion and legacy `deep` labels
 *     during cache/read ingestion. Realtime/push must stay strict.
 *
 * VALIDATION STRATEGY (Migration):
 * - Strict (summaryVersion required): realtime/push/WebSocket emissions.
 * - Legacy (allowLegacy: true) only for cache ingestion paths:
 *   - packages/backend/src/api/routes/extension/summary.ts
 *   - packages/backend/src/ssr/share/story-stream.ts
 *   - packages/backend/src/ssr/admin-reader.ts
 *   - packages/backend/src/ssr/discover.ts
 *   - packages/chrome-plugin/extension/background/reader/fetch/summary-layer.js
 * After migration completes, remove allowLegacy usage.
 * @returns {boolean} True when the payload satisfies the contract shape.
 */
export function isStoryStreamMessage(value, options = {}) {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const allowLegacy = Boolean(options && typeof options === 'object' && options.allowLegacy === true);
  const record = /** @type {Record<string, unknown>} */ (value);
  const type = record.type;
  if (type !== STREAM_MESSAGE_TYPES.STORY_BOOTSTRAP &&
      type !== STREAM_MESSAGE_TYPES.LAYER_SLIDE &&
      type !== STREAM_MESSAGE_TYPES.LAYER_COMPLETE &&
      type !== STREAM_MESSAGE_TYPES.LAYER_FAILED) {
    return false;
  }

  if (typeof record.contentId !== 'string' || record.contentId.length === 0) {
    return false;
  }

  const summaryVersion = record.summaryVersion;
  if (summaryVersion === undefined || summaryVersion === null) {
    if (!allowLegacy) {
      return false;
    }
  } else if (summaryVersion === 'deep') {
    // Cached streams from before the instant-only cutover.
    if (!allowLegacy) {
      return false;
    }
  } else if (summaryVersion !== 'instant') {
    return false;
  }

  if (type === STREAM_MESSAGE_TYPES.STORY_BOOTSTRAP) {
    const classification = record.classification;
    if (!STORY_CLASSIFICATIONS.includes(/** @type {string} */ (classification))) {
      return false;
    }

    const layers = record.availableLayers;
    if (!Array.isArray(layers)) {
      return false;
    }
    const layersToValidate = layers.length === 0 ? ['gist'] : layers;
    for (const layer of layersToValidate) {
      if (!CANONICAL_LAYERS.includes(/** @type {string} */ (layer))) {
        return false;
      }
    }

    return true;
  }

  const layer = record.layer;
  if (!CANONICAL_LAYERS.includes(/** @type {string} */ (layer))) {
    return false;
  }

  if (type === STREAM_MESSAGE_TYPES.LAYER_SLIDE) {
    if (typeof record.sequence !== 'number' || !Number.isInteger(record.sequence) || record.sequence < 1) {
      return false;
    }

    const content = record.content;
    if (typeof content !== 'object' || content === null) {
      return false;
    }

    // Content shape is intentionally permissive to tolerate backend variants and
    // test fixtures. Layer-specific validation is performed downstream where
    // richer context exists.
    return true;
  }

  if (type === STREAM_MESSAGE_TYPES.LAYER_FAILED) {
    if (typeof record.errorCode !== 'string' || record.errorCode.length === 0) {
      return false;
    }
    if (typeof record.errorMessage !== 'string' || record.errorMessage.length === 0) {
      return false;
    }
  }

  return true;
}

// Share helpers + identity URL normalization
export {
  DEFAULT_DOMAIN_KEY,
  sanitizeDomainKey,
  normalizeUrlForIdentity,
  extractYouTubeVideoId
} from './shared/url-utils.js';

// Source mappings (URL normalization + share image derivation)
export {
  SOURCE_MAPPINGS_CONFIG,
  findSourceMapping,
  normalizeSourceUrl,
  getShareImageFallbackPlan,
  getFaviconFallbackPlan
} from './shared/source-mappings.js';

// SimHash + normalization helpers for local-first indexing
export { normalizeContentText } from './shared/content-normalization.js';
export { stripMarkdownToPlainText } from './shared/markdown-utils.js';
export {
  SIMHASH_BITS,
  SIMHASH_HEX_LENGTH,
  IDENTITY_BODY_MAX_CHARS,
  VERSION_BODY_MAX_CHARS,
  tokenizeSimHashText,
  computeSimHash,
  computeSimHashHex,
  simHashToHex,
  parseSimHashHex,
  hammingDistance,
  computeAvailabilityFingerprint,
  computeContentFingerprints
} from './shared/simhash.js';

// Availability key helpers (simhash + YouTube)
export {
  AVAILABILITY_KEY_SEPARATOR,
  AVAILABILITY_KEY_PREFIXES,
  buildSimAvailabilityKey,
  buildYouTubeAvailabilityKey,
  parseAvailabilityKey
} from './shared/availability-keys.js';

// User identity / email resolution across signed-in surfaces.
// `resolveDisplayIdentity` is the single primitive every display surface
// should use; it consults server session, Firebase, bridge, and /api/profile.
// `resolveUserEmail` / `resolveUserEmailWithReload` are retained for the
// sign-in side-effect path that only has a Firebase User in hand
// (e.g. forwarding the email to the extension via `sendAuthImport`).
export {
  resolveDisplayIdentity,
  resolveUserEmail,
  resolveUserEmailWithReload
} from './shared/resolve-user-email.js';
